/**
 * Start EC-543 [FE]
 * Create a redux store for Guest RFQ
 * Update Qty when user add from PLP.
 * */

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { BrandModel, ProductDto, ProductModel, UnitOfMeasureModel } from "@insite/client-framework/Types/ApiModels";
import { removeFromCartEvent } from "@WhiteCap/Common/Utillities/CustomTracking";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const rfqCart = "rfqCart";

export type NewProductModel = {
    id: string;
    imageAltText: string;
    brand: BrandModel | null; // EC-598 Added the brand data to display brand on cart page
    mediumImagePath: string;
    productNumber: string;
    productTitle: string;
    manufacturerItem: string;
    canonicalUrl: string;
    uri: string;
    urlSegment: string;
    qty: number;
    unitOfMeasures: UnitOfMeasureModel[] | null;
    detail: DetailModel;
};

export type DetailModel = {
  name: string;
  modelNumber: string;
  sku: string;
  upcCode: string;
  unspsc: string;
  productCode: string;
  allowAnyGiftCardAmount: boolean;
  canBackOrder: boolean;
  hasMsds: boolean;
  isGiftCard: boolean;
  isHazardousGood: boolean;
  isSpecialOrder: boolean;
  isSubscription: boolean;
  multipleSaleQty: number;
  priceCode: string;
  replacementProductId: string | null;
  roundingRule: string;
  shippingClassification: string;
  shippingHeight: number;
  shippingLength: number;
  shippingWeight: number;
  shippingWidth: number;
  sortOrder: number;
  subscription: unknown | null;
  taxCategory: string;
  taxCode1: string;
  taxCode2: string;
  vatCodeId: string | null;
};



type RFQCartType = {
    quoteCount: number;
    products: NewProductModel[];
    productsIds: string[];
};

const initialState: RFQCartType = {
    quoteCount: 0,
    products: [],
    productsIds: [],
};

const useRFQCartProduct = (quoteCount = 0) => {
    const [cart, setCart] = useState<RFQCartType>(initialState);
    const session = useSelector((state: ApplicationState) => getSession(state));


    useEffect(() => {
        getCartFromLocalStorage();
    }, [quoteCount]);

    useEffect(() => {
        getCartFromLocalStorage();
    }, []);

    const getLocalParsedCart = () => {
        const qCart = localStorage.getItem(rfqCart);
        if (qCart) {
            return JSON.parse(qCart) as RFQCartType;
        }

        localStorage.setItem(rfqCart, JSON.stringify(cart));
        return cart;
    };

    const getCartFromLocalStorage = () => {
        const qCart = getLocalParsedCart();
        setCart(qCart);
    };

    const updateQty = (product: NewProductModel, qty = 1) => {
        const parsedQCart = getLocalParsedCart();

        const newProduct = {
            id: product.id,
            imageAltText: product.imageAltText,
            brand: product.brand,
            mediumImagePath: product.mediumImagePath,
            productNumber: product.productNumber,
            productTitle: product.productTitle,
            manufacturerItem: product.manufacturerItem,
            uri: product.uri,
            urlSegment: product.urlSegment,
            canonicalUrl: product.canonicalUrl,
            unitOfMeasures: product.unitOfMeasures,
            qty,
            detail: product.detail,
        } as NewProductModel;

        if (parsedQCart.productsIds.includes(product.id)) {
            const newParsedQCart = parsedQCart;
            const newProducts = parsedQCart.products.map(item => {
                if (item.id === product.id) {
                    item.qty = item.qty + qty;
                }
                return item;
            });

            newParsedQCart.products = newProducts;
            setCart(newParsedQCart);
            localStorage.setItem(rfqCart, JSON.stringify(newParsedQCart));
        } else {
            parsedQCart.quoteCount = parsedQCart.quoteCount + 1;
            parsedQCart.products.push(newProduct);
            parsedQCart.productsIds.push(newProduct.id);
            setCart(parsedQCart);
            localStorage.setItem(rfqCart, JSON.stringify(parsedQCart));
        }
    };

    const updateQtyFromCart = (product: NewProductModel, qty = 1) => {
        // EC-543 [FE] - update the quantity from the request cart page
        // This function will replace the quantity directly, rather than adding it to the current quantity.
        
        const parsedQCart = getLocalParsedCart();
        if (parsedQCart.productsIds.includes(product.id)) {
            const newParsedQCart = parsedQCart;
            const newProducts = parsedQCart.products.map(item => {
                if (item.id === product.id) {
                    // Below logic, If the quantity is zero, it will not be accepted on the cart page and will automatically remain as one
                    let itemData = {
                        item_id: item.productNumber,
                        item_name: item.productTitle,
                        item_brand: item.brand?.name ? item.brand?.name : "",
                        price: 0,
                        quantity: qty,
                    };
                    if (qty === 0) {
                        item.qty = 1;
                        // itemData = { ...itemData, quantity: 1 };
                    } else {
                        if (qty < item.qty) {
                            itemData = { ...itemData, quantity: item.qty - qty };
                            removeFromCartEvent({
                                event: "remove_from_cart",
                                ecommerce: {
                                    currency: session.currency?.currencyCode,
                                    value: 0,
                                    items: [itemData],
                                },
                            });
                        }
                        item.qty = qty;
                    }
                }
                return item;
            });

            newParsedQCart.products = newProducts;
            setCart(newParsedQCart);
            localStorage.setItem(rfqCart, JSON.stringify(newParsedQCart));
        }
    };

    const setCartToLocalStorage = (product: ProductModel, qty = 1) => {
        const parsedQCart = getLocalParsedCart();

        const newProduct = {
            id: product.id,
            imageAltText: product.imageAltText,
            mediumImagePath: product.mediumImagePath,
            brand: product.brand,
            productNumber: product.productNumber,
            productTitle: product.productTitle,
            uri: product.uri,
            urlSegment: product.urlSegment,
            manufacturerItem: product.manufacturerItem,
            unitOfMeasures: product.unitOfMeasures,
            canonicalUrl: product.canonicalUrl,
            qty,
            detail: product.detail,
        } as NewProductModel;

        if (parsedQCart.productsIds.includes(product.id)) {
            const newParsedQCart = parsedQCart;
            const newProducts = parsedQCart.products.map(item => {
                if (item.id === product.id) {
                    item.qty = item.qty + qty;
                }
                return item;
            });

            newParsedQCart.products = newProducts;
            setCart(newParsedQCart);
            localStorage.setItem(rfqCart, JSON.stringify(newParsedQCart));
        } else {
            parsedQCart.quoteCount = parsedQCart.quoteCount + 1;
            parsedQCart.products.push(newProduct);
            parsedQCart.productsIds.push(newProduct.id);
            setCart(parsedQCart);
            localStorage.setItem(rfqCart, JSON.stringify(parsedQCart));
        }
    };

    const deleteFromLocalStorage = (id: string) => {
        const parsedQCart = getLocalParsedCart();
        const isProductExist = parsedQCart.productsIds.includes(id);
        if (isProductExist) {
            const selectedProduct = parsedQCart.products.filter(item => item.id === id)[0];
            if (selectedProduct) {
                const itemData = {
                    item_id: selectedProduct.productNumber,
                    item_name: selectedProduct.productTitle,
                    item_brand: selectedProduct.brand?.name ? selectedProduct.brand?.name : "",
                    price: 0,
                    quantity: selectedProduct.qty,
                };
                removeFromCartEvent({
                    event: "remove_from_cart",
                    ecommerce: {
                        currency: session.currency?.currencyCode,
                        value: 0,
                        items: [itemData],
                    },
                });
            }
            parsedQCart.products = parsedQCart.products.filter(pro => pro.id !== id);
            parsedQCart.quoteCount = parsedQCart.quoteCount - 1;
            parsedQCart.productsIds = parsedQCart.productsIds.filter(ids => ids !== id);
            setCart(parsedQCart);
            localStorage.setItem(rfqCart, JSON.stringify(parsedQCart));
        }
    };

    const removeQuoteCartFromLocalStorage = () => {
        const parsedQCart = getLocalParsedCart();
        const itemData = [];
        if (parsedQCart.products && parsedQCart.products.length) {
            for (let i = 0; i < parsedQCart.products.length; i++) {
                itemData.push({
                    item_id: parsedQCart.products[i].productNumber,
                    item_name: parsedQCart.products[i].productTitle,
                    item_brand: parsedQCart.products[i].brand?.name ? parsedQCart.products[i].brand?.name : "",
                    price: 0,
                    quantity: parsedQCart.products[i].qty,
                });
            }
        }
        removeFromCartEvent({
            event: "remove_from_cart",
            ecommerce: {
                value: 0,
                currency: session.currency?.currencyCode,
                items: itemData,
            },
        });
        localStorage.removeItem(rfqCart);
        setCart(initialState);
    };

    return {
        cart,
        updateQty,
        updateQtyFromCart,
        setCartToLocalStorage,
        deleteFromLocalStorage,
        removeQuoteCartFromLocalStorage,
        getCartFromLocalStorage,
        getLocalParsedCart,
    };
};

export default useRFQCartProduct;
/**
 * End EC-543 [FE]
 * Create a redux store for Guest RFQ
 * */